import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Loader2, CheckCircle2, XCircle, ScanFace, UserX } from "lucide-react";

const reasonLabel = {
  sem_foto_referencia: "Sem foto de referência no cadastro",
  match_baixo: "Similaridade baixa com a foto do cadastro",
  rosto_nao_detectado: "Rosto não detectado na selfie",
  falha_analise: "Falha na análise automática",
};

export const FaceReviewDialog = ({ open, onOpenChange, onResolved }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);

  const authHeaders = {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/face-reviews`, {
        ...authHeaders, params: { status: "review" },
      });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Erro ao carregar revisões faciais");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const resolve = async (sigId, approve) => {
    setResolvingId(sigId);
    try {
      await axios.post(
        `${API}/admin/face-reviews/${sigId}/resolve`,
        { approve }, authHeaders,
      );
      toast.success(approve
        ? "Assinatura facial aprovada"
        : "Assinatura rejeitada — a convocação voltou a ficar pendente para o funcionário");
      setItems((prev) => prev.filter((i) => i.sig_id !== sigId));
      if (typeof onResolved === "function") onResolved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao resolver revisão");
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="face-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanFace className="w-5 h-5 text-indigo-600" />
            Revisão de assinaturas faciais
          </DialogTitle>
          <DialogDescription>
            Assinaturas feitas por selfie que precisam de conferência humana.
            Aprovar mantém a assinatura; rejeitar reabre a pendência.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-500" data-testid="face-review-empty">
            Nenhuma assinatura facial aguardando revisão. 🎉
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <div
                key={it.sig_id}
                className="rounded-lg border border-slate-200 p-3 flex flex-col sm:flex-row gap-3"
                data-testid={`face-review-item-${it.sig_id}`}
              >
                <div className="flex gap-2 items-start">
                  <div className="text-center">
                    <div className="text-[9px] uppercase text-slate-500 mb-0.5">Cadastro</div>
                    {it.reference_base64 ? (
                      <img
                        src={`data:image/jpeg;base64,${it.reference_base64}`}
                        alt="Referência"
                        className="w-20 h-20 object-cover rounded-md border border-slate-200"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-md border border-dashed border-slate-300 flex items-center justify-center">
                        <UserX className="w-6 h-6 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="text-[9px] uppercase text-slate-500 mb-0.5">Selfie</div>
                    <img
                      src={`data:image/jpeg;base64,${it.selfie_base64}`}
                      alt="Selfie"
                      className="w-20 h-20 object-cover rounded-md border border-slate-200"
                    />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">
                    {it.nome} <span className="text-slate-400 font-normal">({it.matricula})</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    {it.course_key?.replace(/_/g, " ").toUpperCase()}
                    {it.training_date ? ` • ${it.training_date}` : ""}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
                    <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 bg-amber-50">
                      {reasonLabel[it.reason] || it.reason || "Revisão necessária"}
                    </Badge>
                    {it.score != null && it.reason !== "sem_foto_referencia" && (
                      <Badge variant="outline" className="text-[10px]">
                        Similaridade: {it.score}%
                      </Badge>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    Assinado em {it.signed_at ? new Date(it.signed_at).toLocaleString("pt-BR") : "-"}
                  </div>
                </div>
                <div className="flex sm:flex-col gap-2 justify-end">
                  <Button
                    size="sm"
                    onClick={() => resolve(it.sig_id, true)}
                    disabled={resolvingId === it.sig_id}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`face-approve-${it.sig_id}`}
                  >
                    {resolvingId === it.sig_id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> É ele(a)</>}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => resolve(it.sig_id, false)}
                    disabled={resolvingId === it.sig_id}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`face-reject-${it.sig_id}`}
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Rejeitar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
